// Cash interest, staking yield, the weighted dividend yield and the income figures. Ported line for line
// from v1's cashCalc(), cashTotal(), cashInterest(), stakeStats(), stockYields(), monthly(),
// yearRange() and the sums behind the Income panel and the By asset class → Income view.
/* eslint-disable @typescript-eslint/no-explicit-any */
import { addDaysIso, currentYear, days, isoOf, now, today, yearAgoIso, ymd } from './clock';
import { N, assetCcy, cv, isSgx, okC, type State } from './state';
import { sharesOnEx } from './dividends';
import { totals, type Position } from './positions';
import { CLASSES } from './periods';

import { COMP, cashValueOn } from './cashmath';
export { COMP };
export interface CashCalc { apy: number; now: number; accrued: number; annual: number }
export function cashCalc(c: any): CashCalc {
  const amt = N(c.amount), apr = N(c.apr) / 100, n = (COMP[c.comp] || COMP.monthly)[0];
  const apy = apr > 0 ? Math.pow(1 + apr / n, n) - 1 : 0;
  const nowV = cashValueOn(c, today());
  return { apy, now: nowV, accrued: nowV - amt, annual: nowV * apy };
}
export const cashTotal = (S: State) => S.cash.reduce((a, c) => a + cv(S, cashCalc(c).now, c.ccy), 0);
export const cashInterest = (S: State) => S.cash.reduce((a, c) => a + cv(S, cashCalc(c).annual, c.ccy), 0);

export function stakeStats(S: State, s: any): { units: number; value: number; actual: number; days: number } {
  const p = (s.platform || '').trim().toLowerCase(); let units = 0, value = 0;
  for (const i of S.income) {
    if (i.kind !== 'staking' || i.sym !== s.sym || i.date < s.start) continue;
    if (p && (i.platform || '').trim().toLowerCase() !== p) continue;
    units += N(i.qty); value += cv(S, i.gross, i.ccy);
  }
  const d = Math.max(1, days(s.start, today()));
  return { units, value, actual: N(s.qty) > 0 ? units / N(s.qty) * (365 / d) * 100 : NaN, days: d };
}

export interface YieldRow { p: Position; dps: number; wht: number; src: string; gy: number; ny: number; w: number }
export interface Yields { rows: YieldRow[]; missing: string[]; mv: number; gross: number; net: number; gy: number; ny: number }
/** Each stock's dividends per share over the last 12 months against today's price, weighted by its share of
 *  your stock value. Stocks held for a full year use your own recorded payments; newer ones use the
 *  per-share history the daily dividend check gets from Yahoo Finance. */
/** v2 (Ben, 17 Oct 2026): a year of dividends per share that doesn't dip when one payment falls just outside
 *  the last 365 days. A strict 365-day sum drops a quarterly payer to three payments for the days between
 *  last year's payment leaving the window and this year's arriving (SGX, S68, mid-October).
 *  Instead, the year is anchored on the stock's latest payment: everything paid in the 11 months up to and
 *  including it (11 rather than 12, so last year's equivalent payment isn't caught when dates drift by a
 *  couple of weeks). That counts one year of payments however unevenly they're spaced (OCBC and Genting pay
 *  in May and August), and a special dividend in that year counts too. Monthly payers (10+ payments in those
 *  11 months) count their latest 12. If the latest payment is overdue (older than 1.5 of the usual gaps,
 *  + 20 days), the company may have cut or paused, so the strict 365 days apply instead.
 *  (Not "never below the strict sum": when dates drift earlier, the strict 365 days can catch five quarters.)
 *  pays: [{ d: date (ex or pay, consistently), v: per share }]. */
export function yearOfDividends(pays: { d: string; v: number }[], td: string): number {
  const byD: Record<string, number> = {};
  for (const p of pays || []) if (p && p.d <= td && p.v > 0) byD[p.d] = (byD[p.d] || 0) + p.v;
  const ps = Object.entries(byD).sort((a, b) => (a[0] < b[0] ? 1 : -1));   // newest first
  const sum = (xs: [string, number][]) => xs.reduce((a, x) => a + x[1], 0);
  const strict = sum(ps.filter(([d]) => d > addDaysIso(td, -365)));
  if (ps.length < 2) return strict;
  const latest = ps[0][0];
  let year = ps.filter(([d]) => d > addDaysIso(latest, -335));
  if (year.length >= 10) year = ps.slice(0, 12);                           // monthly
  const n = Math.min(12, year.length), gap = 365 / n;
  if (days(latest, td) > gap * 1.5 + 20) return strict;
  return sum(year);
}

export function stockYields(S: State, ps: Position[], divCheck: any, o: { steady?: boolean } = {}): Yields {
  const info: Record<string, any> = {}; ((divCheck && divCheck.stocks) || []).forEach((s: any) => { if (s && s.sym) info[String(s.sym).toUpperCase()] = s; });
  const yStr = isoOf(now() - 365 * 864e5), td = today(), rows: YieldRow[] = [], missing: string[] = [];
  for (const p of ps) {
    if (p.type !== 'stock' || !(p.qty > 0) || !(p.mv > 0) || !((p.price as number) > 0)) continue;
    let rec: any = null; {
      let d = 0, g = 0, t = 0, n = 0; const pays: { d: string; v: number }[] = [], from = o.steady ? addDaysIso(td, -800) : yStr;
      for (const i of S.income) {
        if (i.kind !== 'dividend' || i.sym !== p.sym || !(i.date > from) || i.date > td) continue;
        const q = sharesOnEx(S, p.sym, i.date); if (!(q > 0)) continue;
        if (o.steady) pays.push({ d: i.date, v: cv(S, i.gross, i.ccy) / q });
        if (!(i.date > yStr)) continue;
        d += cv(S, i.gross, i.ccy) / q; g += N(i.gross); t += N(i.tax); n++;
      }
      if (o.steady && pays.length) d = yearOfDividends(pays, td);
      if (n) rec = { dps: d, wht: g > 0 ? t / g : 0, src: 'Your records' };
    }
    const first = S.txns.filter((t) => t.type === 'stock' && t.sym === p.sym && t.side === 'buy').reduce((a: string, t: any) => (a && a < t.date ? a : t.date), '');
    const ix = info[p.sym.toUpperCase()]; let yh: any = null;
    if (ix && ix.yahoo === 'ok' && ix.ttm != null) {
      const d = cv(S, N(o.steady && ix.annual != null ? ix.annual : ix.ttm), okC(ix.ccy) ? ix.ccy : assetCcy(S, 'stock', p.sym));
      if (d / (p.price as number) < 0.4) yh = { dps: d, wht: ix.wht != null ? N(ix.wht) : isSgx(S, p) ? 0 : 0.3, src: 'Yahoo Finance' };
    }
    const use = rec && first && first <= yStr ? rec : yh || rec;
    if (!use) { missing.push(p.sym); continue; }
    if (rec && use === yh) use.wht = rec.wht;
    const gy = use.dps / (p.price as number); rows.push({ p, dps: use.dps, wht: use.wht, src: use.src, gy, ny: gy * (1 - use.wht), w: 0 });
  }
  const mv = rows.reduce((a, r) => a + r.p.mv, 0);
  rows.forEach((r) => { r.w = mv > 0 ? r.p.mv / mv : 0; }); rows.sort((a, b) => b.p.mv - a.p.mv);
  const gross = rows.reduce((a, r) => a + r.p.mv * r.gy, 0), net = rows.reduce((a, r) => a + r.p.mv * r.ny, 0);
  return { rows, missing, mv, gross, net, gy: mv > 0 ? gross / mv * 100 : NaN, ny: mv > 0 ? net / mv * 100 : NaN };
}

export interface Month { key: string; y: number; m: number; yr: string; div: number; stake: number; cpn?: number }
/** The last 12 calendar months, oldest first, with net dividends and staking received in each.
 *  (Labels like "Oct" and "October 2026" are made by the UI from y and m.) */
export function monthly(S: State): Month[] {
  const [cy, cm] = ymd(now()), months: Month[] = [];
  for (let k = 11; k >= 0; k--) {
    const d = new Date(Date.UTC(cy, cm - 1 - k, 1)), y = d.getUTCFullYear(), m = d.getUTCMonth() + 1;
    months.push({ key: y + '-' + String(m).padStart(2, '0'), y, m, yr: k === 11 || m === 1 ? String(y) : '', div: 0, stake: 0 });
  }
  const idx = Object.fromEntries(months.map((m, i) => [m.key, i]));
  for (const i of S.income) {
    const m = idx[(i.date || '').slice(0, 7)]; if (m == null) continue;
    if (i.kind === 'dividend') months[m].div += cv(S, i.gross, i.ccy) - cv(S, i.tax, i.ccy);
    // v2: bond coupons on their own (the field appears once there are coupons)
    else if (i.kind === 'coupon') months.forEach((x, n) => { x.cpn = (x.cpn || 0) + (n === m ? cv(S, i.gross, i.ccy) - cv(S, i.tax, i.ccy) : 0); });
    else months[m].stake += cv(S, i.gross, i.ccy);
  }
  return months;
}

/** Every year from the earliest record to the current one, newest first. */
export function yearRange(dates: unknown[]): string[] {
  const cy = currentYear(); let lo = cy;
  for (const d of dates) { const y = parseInt(String(d || '').slice(0, 4), 10); if (y > 1970 && y < lo) lo = y; }
  const out: string[] = []; for (let y = cy; y >= lo; y--) out.push(String(y)); return out;
}

/** The Income panel's table: net income per source over the last 12 months. */
export function incomeBySource(S: State, show: { div: boolean; stake: boolean }) {
  const yStr = yearAgoIso(), agg: Record<string, any> = {};
  for (const i of S.income) {
    if (!(i.date > yStr) || !(i.kind === 'staking' ? show.stake : show.div)) continue;   // v2: coupons go with dividends
    const k = i.type + ':' + i.sym + ':' + i.kind, a = agg[k] || (agg[k] = { sym: i.sym, key: i.type + ':' + i.sym, kind: i.kind, n: 0, g: 0, t: 0 });
    a.n++; a.g += cv(S, i.gross, i.ccy); a.t += i.kind !== 'staking' ? cv(S, i.tax, i.ccy) : 0;
  }
  return Object.values<{ sym: string; key: string; kind: string; n: number; g: number; t: number }>(agg).sort((a, b) => (b.g - b.t) - (a.g - a.t));
}

/** By asset class → Income: income in the last 12 months and yield per class, plus cash interest ahead. */
export function classIncome(S: State, ps: Position[], divCheck: any, o: { steady?: boolean } = {}) {
  const yStr = isoOf(now() - 365 * 864e5), inc: Record<string, number> = { stock: 0, crypto: 0, metal: 0, bond: 0 };
  for (const i of S.income) if (i.date > yStr && inc[i.type] != null) inc[i.type] += cv(S, i.gross, i.ccy) - (i.kind !== 'staking' ? cv(S, i.tax, i.ccy) : 0);
  const y = stockYields(S, ps, divCheck, o), cash = cashTotal(S), ci = cashInterest(S);
  const rows = CLASSES.map(([k, l]) => {
    const mv = totals(ps, k).mv;
    const yl = k === 'stock' ? (y.rows.length ? y.ny : NaN) : mv > 0 ? inc[k] / mv * 100 : NaN;
    return { k, l, mv, rec: inc[k], yl, how: k === 'stock' ? 'Dividends, weighted yield' : k === 'crypto' ? 'Staking, last 12 months' : k === 'bond' ? 'Coupons, last 12 months' : 'No income' };
  }).filter((r) => r.mv > 0 || r.rec > 0);
  if (cash > 0) rows.push({ k: 'cash', l: 'Cash', mv: cash, rec: ci, yl: cash > 0 ? ci / cash * 100 : NaN, how: 'Interest, next 12 months (est.)' });
  return rows;
}
